import React from "react";
import { App, Modal } from "antd5";
import { useLocation } from "wouter";

import Button from "components/ui/button/Button";
import { useDeleteBidDocument } from "lib/hooks/api/bid/bid_document/useDeleteBidDocument";
import { routes } from "lib/routes";
import { useDeleteBid } from "../../../lib/hooks/api/bid/bids/useDeleteBid";
import { EventNames, useTracking } from "../../../lib/tracking";

import css from "./ReportModals.module.scss";

type Props = {
  bidId: string;
  opportunityId?: string;
  isOpen: boolean;
  onClose: () => void;
};
export function DeleteBidModal({ bidId, isOpen, onClose, opportunityId }: Props) {
  const { mutateAsync: deleteBidMutation } = useDeleteBid({
    onSuccess: async () => {
      if (opportunityId) {
        setLocation(`/${opportunityId}`);
      } else {
        window.location.href = routes.home;
      }
    },
  });

  const { logEvent } = useTracking();
  const [, setLocation] = useLocation();

  async function deleteBid() {
    logEvent(EventNames.qualificationReportDeleted, {
      "Action type": "Button clicked",
    });
    await deleteBidMutation({ input: { bidId } });
  }

  return (
    <Modal
      title="About to delete report"
      open={isOpen}
      centered
      closable={false}
      footer={
        <div className={css.footer}>
          <Button buttonType="secondary" onClick={onClose}>
            Keep report
          </Button>
          <Button buttonStyle="destructive" type="submit" onClick={deleteBid}>
            Delete report
          </Button>
        </div>
      }
      onCancel={onClose}
    >
      Are you sure you want to delete this report? This action cannot be undone.
    </Modal>
  );
}

export function RegenerateScorecardModal({
  isOpen,
  modalText,
  onClose,
  onRegenerate,
}: {
  isOpen: boolean;
  modalText: string;
  onClose: () => void;
  onRegenerate: () => void;
}) {
  return (
    <Modal
      title="Regenerate report"
      open={isOpen}
      centered
      closable={false}
      footer={
        <div className={css.footer}>
          <Button buttonType="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={() => {
              onClose();
              onRegenerate();
            }}
          >
            Regenerate report
          </Button>
        </div>
      }
      onCancel={onClose}
    >
      {modalText}
    </Modal>
  );
}

export function DeleteBidDocumentModal({
  bidId,
  documentId,
  isOpen,
  onClose,
}: {
  bidId: string;
  documentId: string;
  isOpen: boolean;
  onClose: () => void;
}) {
  const { message } = App.useApp();

  const { logEvent } = useTracking();
  const { mutate: deleteDocument } = useDeleteBidDocument({
    onSuccess: async (data) => {
      logEvent(EventNames.qualificationReportDeleted, {
        "Action type": "Button clicked",
        "Deleted document name": data.deleteBidDocument.fileName,
      });
    },
    onError: (error, vars) => {
      console.error(`Error deleting bid document: ${JSON.stringify(error)}`);
      message.error(`Failed to delete document ${vars.request.documentId}`);
    },
  });

  return (
    <Modal
      title="About to delete document"
      open={isOpen}
      closable={false}
      centered
      footer={
        <div className={css.footer}>
          <Button buttonType="secondary" onClick={onClose}>
            Keep document
          </Button>
          <Button
            buttonStyle="destructive"
            type="submit"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              deleteDocument({ request: { documentId, bidId } });
            }}
          >
            Delete document
          </Button>
        </div>
      }
      onCancel={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onClose();
      }}
    >
      Are you sure you want to delete this document? It will remove any source references to it in
      the report.
    </Modal>
  );
}
